import React from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { motion, AnimatePresence } from 'framer-motion';

const models = [
  {
    id: 'hunyuan-3.0',
    name: 'Hunyuan 3.0',
    role: 'State of the art',
    description: 'Latest version. Improved geometry and texture quality.',
    credits: 25,
  },
  {
    id: 'hunyuan-2.0',
    name: 'Hunyuan 2.0',
    role: 'High quality',
    description: 'Best for organic shapes (characters, animals). Prioritizes vivid textures over geometry.',
    credits: 15,
  },
  {
    id: 'trellis',
    name: 'Trellis',
    role: 'High quality',
    description: 'Best for hard surfaces (furniture, props). Prioritizes clean geometry over texture detail.',
    credits: 10,
  },
];

export default function ModelSelector({ selectedModel, setSelectedModel }) {
  const selected = models.find((m) => m.id === selectedModel) || models[0];

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <label className="text-[10px] font-mono uppercase text-muted-foreground tracking-widest">
          Generation Engine
        </label>
      </div>
      <Listbox value={selectedModel} onChange={setSelectedModel}>
        {({ open }) => (
          <div className="relative">
            <Listbox.Button className="relative w-full cursor-pointer rounded-xl bg-black/5 dark:bg-white/5 py-3 pl-4 pr-10 text-left shadow-sm focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm border border-border/50 hover:border-indigo-500/50 hover:bg-indigo-500/5 transition-all duration-200 group">
              <span className="block truncate flex items-center gap-3">
                  <div className="flex flex-col text-left">
                      <div className="flex items-center gap-2">
                          <span className="font-bold text-foreground uppercase tracking-wider text-xs">{selected.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-foreground/5 text-muted-foreground font-medium border border-border/50">{selected.role}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-500 font-medium border border-indigo-500/20">{selected.credits} Credits</span>
                      </div>
                      <span className="text-[10px] text-muted-foreground mt-0.5">{selected.description}</span>
                  </div>
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                <ChevronUpDownIcon
                  className="h-5 w-5 text-muted-foreground"
                  aria-hidden="true"
                />
              </span>
            </Listbox.Button>
            <AnimatePresence>
              {open && (
                <Listbox.Options
                  static
                  as={motion.ul}
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute bottom-full mb-2 max-h-[500px] w-full overflow-auto rounded-xl bg-background/95 backdrop-blur-xl py-1 text-base shadow-xl ring-1 ring-black/5 focus:outline-none sm:text-sm z-50 border border-border/50 origin-bottom"
                >
                  {models.map((model, modelIdx) => (
                    <Listbox.Option
                      key={modelIdx}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-3 pl-4 pr-4 transition-colors ${
                          active ? 'bg-indigo-500/10' : ''
                        }`
                      }
                      value={model.id}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`flex items-center gap-3 ${
                              selected ? 'font-medium' : 'font-normal'
                            }`}
                          >
                            <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                    <span className={`font-bold uppercase tracking-wider text-xs ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>{model.name}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${selected ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' : 'bg-foreground/5 text-muted-foreground border-border/50'}`}>{model.role}</span>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${selected ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' : 'bg-foreground/5 text-muted-foreground border-border/50'}`}>{model.credits} Credits</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground/70 mt-0.5">{model.description}</span>
                            </div>
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-500">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              )}
            </AnimatePresence>
          </div>
        )}
      </Listbox>
    </div>
  );
}
